import { Check, Clock } from 'lucide-react';
import { ButtonLink, cx } from '../../design/index.js';
import { annualSaving, publicPlans } from '../pricing.js';
import { SiteSection } from '../SiteSection.js';

/**
 * The price list (§25, §26), as a salon writes its own.
 *
 * Every salon has a price board at the counter — a service, a row of dots,
 * a price — and that is the form this takes: one line per offer, read top to
 * bottom, with what it includes underneath. It also reads on a phone without
 * three cards side by side to squeeze.
 *
 * Read from the commercial catalogue rather than typed here — see
 * `site/pricing.ts` for why that matters more than it looks.
 *
 * The recommended offer is the middle one of the catalogue's own order, never
 * one picked by name (branching on a plan name is what the billing lint rule
 * exists to prevent), and it is marked once: a word and a rule in the margin.
 *
 * What every offer does is said once, beside the heading, rather than repeated
 * under each: a line lists what its offer adds, so that the difference between
 * two of them is what a reader sees.
 */
export function Pricing({ startUrl }: { readonly startUrl: string }): React.JSX.Element {
  const plans = publicPlans();
  const featured = plans[1]?.code;
  const annual = annualSaving();
  const noSetupFee = plans.every((plan) => plan.setupFee === null);
  // One sentence when every offer charges the same for a minute beyond, rather
  // than the same price repeated once per offer.
  const overages = [...new Set(plans.map((plan) => plan.overagePerMinute))].filter(
    (rate): rate is string => rate !== null,
  );

  return (
    <SiteSection
      id="tarifs"
      title="Tarifs"
      intro={
        <>
          <p>
            Un abonnement mensuel{noSetupFee ? ', sans frais d’installation' : ''}, et rien d’autre.
            Aucune commission sur vos rendez-vous, aucune commission sur vos encaissements.
          </p>
          <p className="text-ui">
            Dans toutes les offres, votre assistante répond à chaque appel, renseigne vos clients,
            prend les rendez-vous dans votre agenda, les confirme par SMS, transfère l’appel quand
            il le faut, et vous gardez l’historique de chaque appel.
          </p>
        </>
      }
    >
      <div className="overflow-hidden rounded-[14px] border border-wall-line bg-surface">
        {plans.map((plan) => {
          const isFeatured = plan.code === featured;
          return (
            <article
              key={plan.code}
              className={cx(
                'relative border-t border-wall-line px-5 py-6 first:border-t-0 sm:px-8 sm:py-8',
                // The recommended offer's rule in the margin, as a pen mark on the board.
                isFeatured &&
                  'before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-teal',
              )}
            >
              {/*
                Name, a row of dots, price: on one baseline, as on the board.
                The dots are an empty element, so its baseline is its bottom
                edge — exactly where a leader belongs.
              */}
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h3 className="sign text-sign-sm text-ink">{plan.name}</h3>
                <span
                  aria-hidden="true"
                  className="hidden min-w-[2rem] flex-1 border-b-2 border-dotted border-line-control sm:block"
                />
                <p className="whitespace-nowrap">
                  <span className="sign-figure text-sign-sm text-ink">
                    {plan.monthly ?? 'Sur devis'}
                  </span>{' '}
                  <span className="text-ui-sm text-ink-muted">HT / mois</span>
                </p>
              </div>

              {isFeatured && (
                <p className="mt-2 text-ui-sm font-medium text-teal-ink">Recommandé</p>
              )}

              <p className="mt-3 max-w-xl text-ui text-ink-muted">{plan.summary}</p>

              <ul className="mt-5 grid gap-x-8 gap-y-2 sm:grid-cols-2">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex gap-2.5 text-ui-sm text-ink">
                    <Check
                      size={16}
                      strokeWidth={2}
                      aria-hidden="true"
                      className="mt-0.5 shrink-0 text-teal-ink"
                    />
                    {feature}
                  </li>
                ))}
              </ul>

              {plan.upcoming.length > 0 && (
                <div className="mt-5">
                  <p className="text-ui-sm font-medium text-ink-muted">Bientôt disponible</p>
                  <ul className="mt-2 grid gap-x-8 gap-y-2 sm:grid-cols-2">
                    {plan.upcoming.map((item) => (
                      <li key={item} className="flex gap-2.5 text-ui-sm text-ink-muted">
                        <Clock
                          size={16}
                          strokeWidth={2}
                          aria-hidden="true"
                          className="mt-0.5 shrink-0"
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {plan.setupFee !== null && (
                <p className="mt-4 text-ui-sm text-ink-muted">
                  Frais d’installation : {plan.setupFee} HT, une fois.
                </p>
              )}

              <div className="mt-6">
                <ButtonLink href={startUrl} variant={isFeatured ? 'primary' : 'secondary'}>
                  Commencer avec {plan.name}
                </ButtonLink>
              </div>
            </article>
          );
        })}
      </div>

      <div className="mt-6 flex flex-col gap-4 border-y border-wall-line py-5 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-ui text-ink-muted">
          <span className="font-medium text-ink">Enterprise.</span> Plusieurs salons, volumes
          importants, engagement de service contractuel, sur devis.
        </p>
        <span className="shrink-0">
          <ButtonLink href="#demo" variant="secondary">
            Demander un devis
          </ButtonLink>
        </span>
      </div>

      <p className="mt-6 max-w-2xl text-ui-sm text-ink-muted">
        {overages.length === 1
          ? `Les minutes au-delà du forfait sont facturées à l’usage, ${overages[0] ?? ''} HT la minute, quelle que soit l’offre.`
          : `Les minutes au-delà du forfait sont facturées à l’usage, au tarif de votre offre :${plans
              .filter((plan) => plan.overagePerMinute !== null)
              .map((plan) => ` ${plan.overagePerMinute ?? ''} pour ${plan.name}`)
              .join(',')}.`}{' '}
        L’engagement annuel donne {annual.percent} de remise, soit l’équivalent de {annual.months}{' '}
        mois.
      </p>
    </SiteSection>
  );
}
